import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { LogIn, UserPlus, Loader2, Camera, X, RefreshCw } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RecentUsersStats from './RecentUsersStats';
import PricingTiers from './PricingTiers';

export default function AuthForm() {
  // Check if user is verified
  const checkUserVerification = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('email_confirmed_at')
        .eq('id', userId)
        .single();
        
      if (error) throw error;
      
      // If email_confirmed_at is null, the user hasn't verified their email
      if (!data?.email_confirmed_at) {
        toast.warning('Please verify your email to access all features', {
          position: "top-right",
          autoClose: 7000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return false;
      }
      
      return true;
    } catch (err) {
      console.error('Error checking verification status:', err);
      return false;
    }
  };
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [niche, setNiche] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isMagicLink, setIsMagicLink] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [currentPrice, setCurrentPrice] = useState('$9.99');
  
  // Fetch current pricing based on user count
  const fetchCurrentPricing = async () => {
    try {
      // The cutoff date - May 29th, 2025 (same as in PricingTiers)
      const cutoffDate = '2025-05-29T00:00:00Z';
      
      // Get count of users who joined before the cutoff date
      const { count: beforeCutoffCount, error: beforeError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .lt('created_at', cutoffDate);
        
      if (beforeError) throw beforeError;
      
      // Get count of paid subscribers who joined on or after the cutoff date
      const { count: paidAfterCutoffCount, error: paidError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', cutoffDate)
        .eq('is_subscribed', true);
        
      if (paidError) throw paidError;
      
      // Calculate the total display count (same logic as PricingTiers)
      if (beforeCutoffCount !== null && paidAfterCutoffCount !== null) {
        const totalUsers = beforeCutoffCount + paidAfterCutoffCount;
        
        // Determine current pricing tier
        if (totalUsers < 500) {
          setCurrentPrice('FREE');
        } else if (totalUsers < 1000) {
          setCurrentPrice('$9.99');
        } else if (totalUsers < 1500) {
          setCurrentPrice('$19.99');
        } else {
          setCurrentPrice('$29.99');
        }
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      // Fallback to default price
      setCurrentPrice('$9.99');
    }
  };

  // Check for any existing session on component mount
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        // User is already logged in
        return;
      }
      
      // Check if there's a stored email pending verification
      const storedEmail = localStorage.getItem('pendingVerificationEmail');
      if (storedEmail) {
        setPendingEmail(storedEmail);
        setEmail(storedEmail);
        setVerificationEmailSent(true);
      }
    };
    
    checkSession();
    fetchCurrentPricing();
  }, []);

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        setError('Profile photo must be less than 2MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return;
      }
      setSelectedAvatar(file);
      setError(null);
    }
  };

  // Function to handle resending verification email
  const handleResendVerification = async () => {
    if (!pendingEmail && !email) {
      setError('No email address found');
      return;
    }
    
    setResendingVerification(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingEmail || email,
        options: {
          emailRedirectTo: `${window.location.origin}/verify`
        }
      });
      
      if (error) throw error;
      
      // Show success message
      setError('Verification email has been resent!');
      
    } catch (err) {
      console.error('Error resending verification:', err);
      if (typeof err === 'object' && err !== null && 'message' in err) {
        setError((err as any).message);
      } else {
        setError('Failed to resend verification email. Please try again.');
      }
    } finally {
      setResendingVerification(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isMagicLink) {
        const { error: magicLinkError } = await supabase.auth.signInWithOtp({
          email,
        });
        if (magicLinkError) throw magicLinkError;
        setMagicLinkSent(true);
      } else if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) {
          // Check if this is an 'Email not confirmed' error
          if (error.message && error.message.includes('Email not confirmed')) {
            // Sign in the user anyway despite not being verified
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email,
              password
            });
            
            if (signInError) throw signInError;
            
            // Show verification toast message
            toast.warning('Please verify your email for full account access', {
              position: "top-right",
              autoClose: 7000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            
            // Send a verification email again
            try {
              await supabase.auth.resend({
                type: 'signup',
                email,
                options: {
                  emailRedirectTo: `${window.location.origin}/verify`
                }
              });
            } catch (resendError) {
              console.error('Error resending verification email:', resendError);
            }
            
            return;
          } else {
            throw error;
          }
        }
        
        // Check if user is verified and show toast if not
        if (data?.user) {
          checkUserVerification(data.user.id);
        }
      } else {
        if (!displayName.trim()) {
          throw new Error('Display name is required');
        }
        
        // Check if phone number is provided
        if (!phoneNumber.trim()) {
          throw new Error('Phone number is required');
        }

        // First sign up the user - enable email confirmation
        const { data: { user }, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/verify`,
            data: {
              display_name: displayName,
              phone_number: phoneNumber || null,
              niche: niche || null
            }
          }
        });

        if (signUpError) throw signUpError;
        if (!user) throw new Error('Signup failed');
        
        // Show toast notification for email verification
        toast.info('Please verify your email - verification link sent!', {
          position: "top-right",
          autoClose: 7000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // If email confirmation was sent, store it for later reference but continue with signup
        if (user && (!user.identities || user.identities.length === 0)) {
          // Store the email for verification reference
          localStorage.setItem('pendingVerificationEmail', email);
          setPendingEmail(email);
          // We'll continue with the account creation instead of stopping
        }

        // Upload avatar if selected
        let avatarUrl = '';
        if (selectedAvatar) {
          const fileExt = selectedAvatar.name.split('.').pop();
          const fileName = `${user.id}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(fileName, selectedAvatar);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(fileName);
          
          avatarUrl = publicUrl;
        }

        // Get auth webhook URL
        const { data: webhookData, error: webhookError } = await supabase
          .from('webhooks')
          .select('url')
          .eq('type', 'auth_webhook')
          .single();

        if (webhookError) {
          console.error('Error fetching webhook:', webhookError);
        }

        // Update profile with display name and avatar
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            display_name: displayName,
            avatar_url: avatarUrl || null,
            phone_number: phoneNumber || null,
            niche: niche || null,
            is_subscribed: false
          })
          .eq('id', user.id);

        if (updateError) throw updateError;

        // Send data to webhook if URL exists
        if (webhookData?.url) {
          try {
            const joined_at = new Date().toISOString();
            await fetch(webhookData.url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                display_name: displayName,
                email: email,
                phone_number: phoneNumber || null,
                niche: niche || null,
                bio: null,
                joined_at: joined_at
              }),
            });
          } catch (webhookErr) {
            console.error('Error calling webhook:', webhookErr);
          }
        }
        
        // Redirect to subscription page to use our embedded checkout flow
        window.location.href = '/subscription';
      }
    } catch (err) {
      console.error('Authentication error:', err);
      // Properly handle Supabase auth errors
      if (typeof err === 'object' && err !== null) {
        // Check if it's a Supabase auth error with message property
        if ('message' in err) {
          const message = (err as any).message;
          
          // Handle specific error cases with more user-friendly messages
          if (message.includes('User already registered')) {
            setError('An account with this email already exists');
          } else if (message.includes('Invalid login credentials')) {
            setError('Invalid email or password');
          } else if (message.includes('Email not confirmed')) {
            // Sign in anyway but show a toast notification
            try {
              const { error: signInError } = await supabase.auth.signInWithPassword({
                email,
                password
              });
              
              if (signInError) {
                setError('Unable to sign in. Please try again.');
                return;
              }
              
              // Show verification toast message
              toast.warning('Please verify your email for full account access', {
                position: "top-right",
                autoClose: 7000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
              });
              
              return; // exit the error handling
            } catch (signInErr) {
              setError('Unable to sign in. Please try again.');
              return;
            }
          } else {
            // Use the error message as is
            setError(message);
          }
        } else if ('error' in err && (err as any).error && 'message' in (err as any).error) {
          // Some Supabase errors are nested with { error: { message: '...' } }
          setError((err as any).error.message);
        } else {
          // Fallback for other error types
          setError('Authentication failed. Please try again.');
        }
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="min-h-screen w-full flex items-center justify-center px-1 sm:px-0 py-8">
      <div className="max-w-6xl w-full grid grid-cols-1 sm:grid-cols-[1.2fr_1fr] gap-8 px-4 sm:px-8 mx-auto items-start">
          
          {/* Left Section: Video + Stats (Desktop Only) */}
          <div className="hidden sm:flex w-full flex-col gap-6">
          <div className="aspect-video rounded-2xl overflow-hidden bg-[#c9fffc]/5 backdrop-blur-sm border border-[#c9fffc]/10 shadow-2xl">
              <iframe
                width="100%"
                height="100%"
                src={import.meta.env.VITE_INTRO_LINK}
                title="Community App Introduction"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
  
            <div className="p-4 bg-[#c9fffc]/5 backdrop-blur-sm border border-[#c9fffc]/10 rounded-xl shadow-lg">
              <RecentUsersStats />
              <div className="mt-3 pt-3 border-t border-[#c9fffc]/10">
                <PricingTiers />
              </div>
            </div>
          </div>
  
          {/* Auth Form Section */}
          <div className="w-full max-w-md mx-auto">

          <div className="flex-1 relative w-full max-w-full sm:max-w-md px-1 sm:px-0">
              <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-24 h-24 rounded-full bg-gray-900 p-4 ring-8 ring-[#c9fffc]/10 z-20">
                <img
                  src="https://i.postimg.cc/25s922XT/image.png"
                  alt="Community Logo"
                  className="w-16 h-16 object-contain"
                />
              </div>
  
              <div className="w-full p-6 pt-20 sm:p-8 sm:pt-16 bg-transparent backdrop-blur-sm border border-[#c9fffc]/10 rounded-2xl shadow-2xl z-10">
  
                <h2 className="text-3xl font-bold text-white mb-2 text-center">
                  {isLogin ? 'Welcome Back' : 'Create Account'}
                </h2>
                <p className="text-gray-400 text-center mb-8">
                  {isLogin
                    ? 'Sign in to continue to Community'
                    : 'Create a new account to get started'}
                </p>
  
                {/* Mobile Only - YouTube and Stats */}
                <div className="sm:hidden mb-10 -mx-4">
                  <div className="aspect-[16/9] sm:aspect-[5/2] rounded-xl overflow-hidden bg-white/5 backdrop-blur-sm border border-[#c9fffc]/10">
                    <iframe
                      width="100%"
                      height="100%"
                      src={import.meta.env.VITE_INTRO_LINK}
                      title="Community App Introduction"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
  
                  <div className="mt-4 p-4 bg-transparent backdrop-blur-sm border border-[#c9fffc]/10 rounded-xl shadow-lg">
                    <RecentUsersStats />
                    <div className="mt-3 pt-3 border-t border-[#c9fffc]/10">
                      <PricingTiers />
                    </div>
                  </div>
                </div>
            
            {loading ? (
              <div className="text-center py-10">
                <Loader2 className="w-12 h-12 mx-auto text-[#c9fffc] animate-spin" />
                <p className="text-white mt-4">Loading...</p>
              </div>
            ) : verificationEmailSent ? (
              <div className="text-center py-10">
                <div className="w-20 h-20 bg-[#c9fffc]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-[#c9fffc]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Verify your email</h2>
                <p className="text-gray-400 mb-4">We've sent a verification link to <span className="text-[#c9fffc]">{pendingEmail || email}</span></p>
                <p className="text-gray-400 mb-6">Please check your inbox and click the link to verify your account.</p>
                
                {error && (
                  <div className={`text-sm rounded-lg p-3 mb-4 ${error.includes('has been resent') ? 'text-green-400 bg-green-500/10 border border-green-500/20' : 'text-red-400 bg-red-500/10 border border-red-500/20'}`}>
                    {error}
                  </div>
                )}
                
                <div className="flex justify-between mb-8">
                  <button
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                    className="flex items-center text-[#c9fffc] hover:text-white transition-colors disabled:opacity-50"
                  >
                    {resendingVerification ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Resend verification email
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setVerificationEmailSent(false);
                      localStorage.removeItem('pendingVerificationEmail');
                      setPendingEmail(null);
                    }}
                    className="text-[#c9fffc] hover:text-white transition-colors"
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            ) : magicLinkSent ? (
              <div className="text-center space-y-4">
                <div className="bg-[#c9fffc]/10 border border-[#c9fffc]/20 rounded-lg p-4">
                  <p className="text-white">Magic link sent! ✨</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Check your email for the login link
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMagicLinkSent(false);
                    setIsMagicLink(false);
                    setEmail('');
                  }}
                  className="text-[#c9fffc] hover:text-white text-sm transition-colors"
                >
                  Try another method
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
              {!isLogin && (
                <>
                  <div className="flex gap-6 items-start">
                    <div className="flex-1">
                      <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-1">
                        Display Name
                      </label>
                      <input
                        id="displayName"
                        type="text"
                        required
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="block w-full px-1 py-3 bg-gray-900 border border-[#c9fffc]/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#c9fffc] focus:border-transparent transition-colors"
                        placeholder="How should we call you?"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Profile Photo
                      </label>
                      <div className="relative">
                        <div className="w-[72px] h-[72px] rounded-full overflow-hidden bg-gray-800/50 backdrop-blur-sm border border-[#c9fffc]/10">
                          {selectedAvatar ? (
                            <img
                              src={URL.createObjectURL(selectedAvatar)}
                              alt="Selected avatar"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Camera className="w-8 h-8" />
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarSelect}
                          ref={fileInputRef}
                          className="hidden"
                        />
                        {selectedAvatar ? (
                          <button
                            type="button"
                            onClick={() => setSelectedAvatar(null)}
                            className="absolute bottom-0 right-0 p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute bottom-0 right-0 p-2 bg-[#c9fffc] rounded-full hover:bg-[#a0fcf9] transition-colors"
                          >
                            <Camera className="w-4 h-4 text-gray-900" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">

                    
                    <div className="flex gap-6">
                      <div className="flex-1">
                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-300 mb-1">
                          Phone Number (Required)
                        </label>
                        <input
                          id="phoneNumber"
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          required
                          className="block w-full px-1 py-3 bg-gray-900 border border-gray-400/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-gray focus:border-transparent transition-colors"
                          placeholder="Your phone number with country code"
                        />
                        <p className="text-xs text-gray-300 mt-1">Include country code (e.g., +1 for US/Canada)</p>
                      </div>
                      <div className="flex-1">
                        <label htmlFor="niche" className="block text-sm font-medium text-gray-300 mb-1">
                          Niche (Optional)
                        </label>
                        <input
                          id="niche"
                          type="text"
                          value={niche}
                          onChange={(e) => setNiche(e.target.value)}
                          className="block w-full px-4 py-3 bg-gray-900 border border-[#c9fffc]/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#c9fffc] focus:border-transparent transition-colors"
                          placeholder="Your area of expertise"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-4 py-3 bg-gray-900 border border-[#c9fffc]/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#c9fffc] focus:border-transparent transition-colors"
                />
              </div>

              {!isMagicLink && <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-4 py-3 bg-gray-900 border border-[#c9fffc]/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#c9fffc] focus:border-transparent transition-colors"
                />
              </div>}

              {error && (
                <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  {error}
                </div>
              )}



              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 bg-[#c9fffc] text-gray-900 rounded-xl hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] focus:ring-offset-2 focus:ring-offset-gray-900 font-medium shadow-lg shadow-[#c9fffc]/10 transition-all"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isLogin ? (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    {isMagicLink ? 'Send Magic Link' : 'Sign In'}
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    {currentPrice === 'FREE' ? 'Join for Free' : `Join for ${currentPrice}/Month`}
                  </>
                )}
              </button>

              {isLogin ? (
  <div className="space-y-3 mt-6">
    <button
      type="button"
      onClick={() => setIsLogin(false)}
      className="w-full text-white font-medium bg-[#c9fffc]/10 border border-[#c9fffc]/20 rounded-xl py-3 hover:bg-[#c9fffc]/20 transition-colors"
    >
      Don’t have an account? Create one
    </button>

    <div className="flex justify-between items-center">
      <button
        type="button"
        onClick={() => setIsMagicLink(!isMagicLink)}
        className="text-sm text-gray-400 hover:text-white transition-colors"
      >
        {isMagicLink ? 'Use password instead' : 'Use magic link instead'}
      </button>
      
      {!isMagicLink && (
        <a
          href="/reset-password"
          className="text-sm text-[#c9fffc] hover:text-white transition-colors"
        >
          Forgot Password?
        </a>
      )}
    </div>
  </div>
) : (
  <div className="mt-6">
    <button
      type="button"
      onClick={() => setIsLogin(true)}
      className="w-full text-gray-400 hover:text-white text-sm transition-colors"
    >
      Already have an account? Sign in
    </button>
  </div>
)}

                </form>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}